import { useEffect, useState, type FormEvent } from 'react';
import type { HappyHourDTO } from '@six-pockets/shared';
import { manageApi } from '@/api';
import { CheckIcon } from '@/components/icons';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button, InlineAlert, MoneyInput, Spinner, Switch, TextField, TimeInput, WeekdayPicker } from '@/components/ui';
import { parseWhole } from '@/features/manage/draft';
import styles from '@/styles/pages/HappyHourPage/style.scss';

/** How long the "Saved" tick shows on the button. */
const SAVED_MS = 1500;

/** The settings as one comparable string, to tell whether the form differs from what is saved. */
const settingsKey = (h: HappyHourDTO) =>
  JSON.stringify([h.enabled, [...h.days].sort((a, b) => a - b), h.startTime, h.endTime, h.discountPercent, h.maxDiscount]);

/**
 * Admin: the happy hour settings. The bill modal's "Happy hours discount" tick gives the discount rate on the
 * table time played between the start and end time on the days picked, up to the maximum per bill (worked out by
 * the server). Switched off, no bill gets it; the rest of the settings are kept for when it is turned back on.
 */
export function HappyHourPage() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [days, setDays] = useState<number[]>([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [percent, setPercent] = useState('');
  const [max, setMax] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** What the server has now; Save is enabled only while the form differs from it. */
  const [savedKey, setSavedKey] = useState('');

  useEffect(() => {
    const ctrl = new AbortController();
    manageApi.happyHour.get(ctrl.signal).then(
      (h) => {
        setEnabled(h.enabled);
        setDays(h.days);
        setStartTime(h.startTime);
        setEndTime(h.endTime);
        setPercent(String(h.discountPercent));
        setMax(h.maxDiscount ? String(h.maxDiscount) : '');
        setSavedKey(settingsKey(h));
        setLoading(false);
      },
      (err) => {
        if (ctrl.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Could not load the happy hour settings');
        setLoading(false);
      },
    );
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), SAVED_MS);
    return () => clearTimeout(timer);
  }, [saved]);

  const discountPercent = parseWhole(percent);
  const maxDiscount = max.trim() ? parseWhole(max) : 0;
  const blocked = enabled && days.length === 0
    ? 'Pick at least one day'
    : !startTime
    ? 'Enter the start time'
    : !endTime
      ? 'Enter the end time'
      : startTime === endTime
        ? 'The start and end time cannot be the same'
        : discountPercent === null || discountPercent > 100
          ? 'Enter the discount rate, 0 to 100'
          : maxDiscount === null
            ? 'Enter the maximum discount in whole rupees'
            : undefined;

  // A field that doesn't parse can't match the saved settings, so it counts as a change (and `blocked` explains it).
  const changed =
    settingsKey({
      enabled,
      days,
      startTime,
      endTime,
      discountPercent: discountPercent ?? NaN,
      maxDiscount: maxDiscount ?? NaN,
    }) !== savedKey;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (blocked || !changed || saving) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const next = await manageApi.happyHour.save({
        enabled,
        days,
        startTime,
        endTime,
        discountPercent: discountPercent!,
        maxDiscount: maxDiscount!,
      });
      setSavedKey(settingsKey(next));
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the happy hour settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title="Happy Hours Settings" />

      {error && <InlineAlert message={error} onDismiss={() => setError(null)} />}

      {loading ? (
        <p className={styles.muted}>Loading…</p>
      ) : (
        <form className={styles.form} onSubmit={(e) => void submit(e)}>
          <Switch
            label="Happy hours"
            hint={enabled ? 'On: bills get the discount below' : 'Off: no bill gets a happy hour discount'}
            checked={enabled}
            onChange={setEnabled}
          />
          <WeekdayPicker legend="Days" value={days} onChange={setDays} />
          <TimeInput label="Start time" value={startTime} onChange={setStartTime} />
          <TimeInput label="End time" value={endTime} onChange={setEndTime} />
          <TextField
            label="Discount rate (%)"
            type="number"
            inputMode="numeric"
            min={0}
            max={100}
            step={1}
            mono
            suffix="%"
            hint="Off the table time played in happy hour"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
          />
          <MoneyInput label="Maximum discount" hint="Per bill. Leave empty for no limit" value={max} onChange={setMax} />
          <div className={styles.actions}>
            {/* Saving (spinner) → Saved (tick) on the button itself; no toast. */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              // Disabled with nothing changed or something to fix; kept as is while Saving… / Saved shows.
              disabled={(!!blocked || !changed) && !saving && !saved}
              title={blocked ?? (changed ? undefined : 'No changes to save')}
              aria-busy={saving}
              icon={saving ? <Spinner size={16} /> : saved ? <CheckIcon size={18} /> : undefined}
            >
              <span role="status">{saving ? 'Saving…' : saved ? 'Saved' : 'Save changes'}</span>
            </Button>
          </div>
        </form>
      )}
    </>
  );
}
